/**
 * Operations — Fulfillment-Operations-Dashboard mit Tages- und Periodenansicht.
 *
 * Features:
 * - KPIs: Sendungen, offene/abgeschlossene Waves, Durchschnittsdauer
 * - Tagesverlauf-Charts (Stacked Bar, Area), Partner-Verteilung (Pie)
 * - Standort-Filter, Auto-Refresh, Datumsbereich-Auswahl
 *
 * @page /operations
 * @roles admin, manager
 * @api /api/fulfillment/operations, /api/standorte
 * @i18n operations
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getDateRange } from '../utils/dateUtils';
import DateRangeFilter from '../components/DateRangeFilter';
import { PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Legend,
} from 'recharts';
import { Weight, TrendingUp, Package, Truck } from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────

function api(path) {
  return fetch(path, { headers: { 'Content-Type': 'application/json' } }).then(r => r.json());
}

function fmtDate(d) {
  if (!d) return '';
  const p = d.split('-');
  return `${p[2]}.${p[1]}`;
}

function fmtDateFull(d) {
  if (!d) return '';
  const p = d.split('-');
  return `${p[2]}.${p[1]}.${p[0]}`;
}

const CHART_COLORS = {
  done: '#338edc',
  todo: '#22D3EE',
  offen: '#22D3EE',
  abgeschlossen: '#338edc',
};

const PARTNER_COLORS = [
  '#338edc', '#4D9CE0', '#22D3EE', '#6366F1', '#E5E7EB',
  '#66ABE5', '#8FC2EE', '#67E8F9', '#818CF8', '#B8D8F5',
  '#06b6d4', '#4F46E5', '#9CA3AF', '#7DD3FC', '#6B7280',
];

// ─── Custom Tooltip ──────────────────────────────────────────────

function ChartTooltip({ active, payload, label, fmtNum, t }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-elevated border border-line-hover rounded-card px-3 py-2 shadow-xl text-xs">
      <div className="text-content-tertiary mb-1 font-medium">{typeof label === 'string' && label.includes('-') ? fmtDateFull(label) : label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-content-secondary">{p.name}:</span>
          <span className="font-mono font-bold text-content">{fmtNum(p.value)}</span>
        </div>
      ))}
      {payload.length === 2 && (
        <div className="border-t border-line-hover mt-1 pt-1 text-content-secondary">
          {t('operations.tooltip.total')}: <span className="font-mono text-content">{fmtNum((payload[0]?.value || 0) + (payload[1]?.value || 0))}</span>
        </div>
      )}
    </div>
  );
}

function StackedTooltip({ active, payload, label, fmtNum, t }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-surface-elevated border border-line-hover rounded-card px-3 py-2 shadow-xl text-xs">
      <div className="text-content-tertiary mb-1 font-medium">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || p.fill }} />
          <span className="text-content-secondary">{p.name}:</span>
          <span className="font-mono font-bold text-content">{fmtNum(p.value)}</span>
        </div>
      ))}
      <div className="border-t border-line-hover mt-1 pt-1 text-content-secondary">
        {t('operations.tooltip.total')}: <span className="font-mono text-content">{fmtNum(total)}</span>
      </div>
    </div>
  );
}

function SimpleTooltip({ active, payload, fmtNum }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-surface-elevated border border-line-hover rounded-card px-3 py-2 shadow-xl text-xs">
      <div className="text-content-secondary">{p.payload?.name || p.payload?.tag || p.name}</div>
      <div className="font-mono font-bold text-content">{fmtNum(p.value)}</div>
    </div>
  );
}

// ─── Zeitraum presets (now using central dateUtils) ──────────────

// ═══════════════════════════════════════════════════════════════════
// OPERATIONS DASHBOARD
// ═══════════════════════════════════════════════════════════════════

export default function Operations() {
  const { t, locale } = useLanguage();
  const [preset, setPreset] = useState('heute');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(60000); // 1min default
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  const [standorte, setStandorte] = useState([]);
  const [standortId, setStandortId] = useState('alle');
  const [weightStats, setWeightStats] = useState(null);
  const [weightTrend, setWeightTrend] = useState([]);
  const [auftragePaketeTrend, setAuftragePaketeTrend] = useState(null);

  // Locale-aware number formatter
  const fmtNum = useCallback((n) => {
    if (n == null) return '\u2013';
    return n.toLocaleString(locale);
  }, [locale]);

  // Initialize date range + load standorte
  useEffect(() => {
    const r = getDateRange('heute');
    setVon(r.von);
    setBis(r.bis);
    api('/api/standorte').then((s) => { if (Array.isArray(s)) setStandorte(s); });
  }, []);

  // Range change handler
  const handleRangeChange = useCallback(({ von: v, bis: b, preset: p }) => {
    setVon(v); setBis(b); setPreset(p);
  }, []);

  // Fetch data — single source: /api/fulfillment/operations
  const fetchData = useCallback(async () => {
    if (!von || !bis) return;
    setLoading(true);
    try {
      let url = `/api/fulfillment/operations?von=${von}&bis=${bis}`;
      if (standortId !== 'alle') url += `&standort_id=${standortId}`;
      const [res, wStats, wTrend, apTrend] = await Promise.all([
        api(url),
        api('/api/performance/gewicht-stats?days=30').catch(() => null),
        api('/api/performance/gewicht-trend?days=30').catch(() => []),
        api(`/api/operations/auftraege-pakete-trend?von=${von}&bis=${bis}`).catch(() => null),
      ]);
      if (res && !res.error) {
        setData(res);
        setLastUpdate(new Date());
      }
      if (wStats && !wStats.error) setWeightStats(wStats);
      if (Array.isArray(wTrend)) setWeightTrend(wTrend);
      if (apTrend && !apTrend.error) setAuftragePaketeTrend(apTrend);
    } catch { /* ignore */ }
    setLoading(false);
  }, [von, bis, standortId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [refreshInterval, fetchData]);

  // ─── Heatmap color scale ───
  const stundenAnzahlTage = data?.stunden_anzahl_tage || 1;
  const isMultiDay = stundenAnzahlTage > 1;
  const maxHour = useMemo(() => {
    if (!data?.pro_stunde) return 1;
    return Math.max(1, ...data.pro_stunde.map(h => isMultiDay ? h.avg_pro_tag : h.anzahl));
  }, [data, isMultiDay]);

  function heatColor(val) {
    if (val === 0) return 'rgba(255,255,255,0.02)';
    const intensity = val / maxHour;
    if (intensity < 0.33) return `rgba(51,142,220,${0.15 + intensity * 0.5})`;
    if (intensity < 0.66) return `rgba(139,92,246,${0.2 + intensity * 0.5})`;
    return `rgba(236,72,153,${0.3 + intensity * 0.5})`;
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  const kpis = data?.kpis;
  const hasData = data?.has_data;
  const datenStatus = data?.daten_status;
  const tagesImports = data?.tages_imports;
  const monatVersendet = data?.monat_versendet;

  return (
    <div className="p-page space-y-section">
      {/* Header */}
      <PageHeader title={t('operations.title')} subtitle={t('operations.subtitle')} />

      {/* ── FILTER BAR ────────────────────────────────────────── */}
      <DateRangeFilter
        von={von} bis={bis} preset={preset}
        onChange={handleRangeChange}
      >
        {/* Standort filter */}
        <select value={standortId} onChange={(e) => setStandortId(e.target.value)}
          className="bg-surface-inset border border-line-hover rounded-lg px-3 py-1.5 text-xs text-content focus:outline-none focus:border-primary-500/40">
          <option value="alle">{t('operations.all_locations')}</option>
          {standorte.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <div className="flex-1" />

        {/* Auto-Refresh */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-content-secondary uppercase tracking-wider">{t('operations.auto_refresh')}</span>
          <div className="flex bg-surface-inset rounded-card p-0.5 border border-line-hover">
            {[
              { val: 0, label: t('operations.off') },
              { val: 60000, label: '1m' },
              { val: 300000, label: '5m' },
              { val: 900000, label: '15m' },
            ].map(r => (
              <button key={r.val} onClick={() => setRefreshInterval(r.val)}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  refreshInterval === r.val ? 'bg-surface-active text-content' : 'text-content-tertiary hover:text-content-secondary'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Letztes Update */}
        {lastUpdate && (
          <div className="text-[10px] text-content-tertiary font-mono">
            {lastUpdate.toLocaleTimeString(locale)}
          </div>
        )}

        {/* Refresh button */}
        <button onClick={fetchData}
          className={`p-2 rounded-lg hover:bg-surface-hover text-content-secondary hover:text-content transition-colors ${loading ? 'animate-spin' : ''}`}
          title={t('operations.refresh_now')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 2v6h-6M3 12a9 9 0 0115.36-6.36L21 8M3 22v-6h6M21 12a9 9 0 01-15.36 6.36L3 16" />
          </svg>
        </button>
      </DateRangeFilter>

      {/* ── DATEN-LÜCKEN BANNER ────────────────────────────────── */}
      {datenStatus && !datenStatus.ist_aktuell && datenStatus.luecke_tage > 0 && (
        <div className="bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.2)] rounded-card px-4 py-3 flex items-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500 flex-shrink-0">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div className="text-sm text-amber-500">
            <span className="font-medium">{t('operations.data_gap')}</span>{' '}
            {t('operations.data_gap_detail_v2', {
              waves: fmtDateFull(datenStatus.waves_bis),
              packs: fmtDateFull(datenStatus.packs_bis),
              days: datenStatus.luecke_tage,
            })}
          </div>
        </div>
      )}

      {/* Fallback-Hinweis wenn Zeitraum automatisch erweitert wurde */}
      {data?.fallback_range && (
        <div className="bg-[rgba(51,142,220,0.06)] border border-[rgba(51,142,220,0.2)] rounded-card px-4 py-2.5 text-sm text-primary-400 flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {data.fallback_range.grund} — {t('operations.fallback_showing')} <span className="font-medium">{fmtDateFull(data.fallback_range.von)} – {fmtDateFull(data.fallback_range.bis)}</span>
        </div>
      )}

      {/* ── KPI CARDS — Row 1: Warehouse ───────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          label={t('operations.kpi.packages')}
          value={kpis?.pakete_gesamt}
          sub={data ? `${fmtDateFull(data.periode?.effective_bis || data.periode?.von)}${data.periode?.von !== data.periode?.bis ? ' – ' + fmtDateFull(data.periode?.bis) : ''}` : null}
          color="#338edc"
          loading={loading}
          fmtNum={fmtNum}
        />
        <KpiCard
          label={t('operations.kpi.waves')}
          value={kpis?.waves}
          sub={kpis?.picks > 0 ? `${fmtNum(kpis.picks)} Picks` : null}
          color="#4D9CE0"
          loading={loading}
          fmtNum={fmtNum}
        />
        <KpiCard
          label={t('operations.kpi.data_days')}
          value={kpis?.tage_mit_daten}
          sub={kpis?.werktage ? t('operations.kpi.of_working_days', { n: kpis.werktage }) : null}
          color="#f59e0b"
          loading={loading}
          fmtNum={fmtNum}
        />
        <KpiCard
          label={t('operations.kpi.avg_per_workday')}
          value={kpis?.avg_pro_tag}
          sub={kpis?.werktage ? t('operations.kpi.over_workdays', { n: kpis.werktage }) : null}
          color="#06b6d4"
          loading={loading}
          fmtNum={fmtNum}
        />
        <KpiCard
          label={t('performance.paketgewicht')}
          value={weightStats?.avg_gewicht ? weightStats.avg_gewicht.toFixed(2) + ' kg' : null}
          sub={weightStats?.pakete_mit_gewicht ? t('performance.basedOnDays', { days: 30 }) : null}
          color="#338edc"
          loading={loading && !weightStats}
          fmtNum={(v) => v ?? '\u2013'}
        />
      </div>

      {/* ── KPI CARDS — Row 2: Imports + Monat ───────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Tages-Imports */}
        <div className="bg-surface-card border border-line rounded-card p-4 relative overflow-hidden shadow-card">
          <div className="absolute top-0 left-0 w-1 h-full rounded-l-card bg-emerald-500" />
          <div className="pl-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" />
              <div className="text-[10px] text-content-secondary uppercase tracking-wider font-medium">
                {t('operations.imports.title')} {tagesImports?.waves?.datum ? `(${fmtDateFull(tagesImports.waves.datum)})` : ''}
              </div>
            </div>
            {loading && !tagesImports ? (
              <div className="h-8 w-40 bg-surface-inset rounded animate-pulse" />
            ) : tagesImports ? (
              <>
                <div className="flex items-baseline gap-3">
                  <div>
                    <span className="font-mono font-bold text-2xl text-content">{fmtNum(tagesImports.waves.count)}</span>
                    <span className="text-xs text-content-secondary ml-1">{t('operations.imports.waves')}</span>
                  </div>
                  <div>
                    <span className="font-mono font-bold text-lg text-content-secondary">{fmtNum(tagesImports.waves.picks)}</span>
                    <span className="text-xs text-content-secondary ml-1">{t('operations.imports.picks')}</span>
                  </div>
                  <div>
                    <span className="font-mono font-bold text-lg text-content-secondary">{fmtNum(tagesImports.packs.count)}</span>
                    <span className="text-xs text-content-secondary ml-1">{t('operations.imports.packs')}</span>
                  </div>
                </div>
                {!tagesImports.ist_heute && (
                  <div className="text-[10px] text-amber-500 mt-1.5">{t('operations.imports.not_today')}</div>
                )}
              </>
            ) : (
              <div className="text-sm text-content-tertiary">{t('common.no_data')}</div>
            )}
          </div>
        </div>

        {/* Monat versendet */}
        <div className="bg-surface-card border border-line rounded-card p-4 relative overflow-hidden shadow-card">
          <div className="absolute top-0 left-0 w-1 h-full rounded-l-card bg-primary-500" />
          <div className="pl-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" />
              <div className="text-[10px] text-content-secondary uppercase tracking-wider font-medium">
                {monatVersendet ? new Date(monatVersendet.monat + '-01').toLocaleDateString(locale, { month: 'long', year: 'numeric' }) : t('operations.month.month_fallback')} — {t('operations.month.title')}
              </div>
            </div>
            {loading && !monatVersendet ? (
              <div className="h-8 w-40 bg-surface-inset rounded animate-pulse" />
            ) : monatVersendet ? (
              <>
                <div className="flex items-baseline gap-3">
                  <div>
                    <span className="font-mono font-bold text-2xl text-content">{fmtNum(monatVersendet.packs)}</span>
                    <span className="text-xs text-content-secondary ml-1">{t('operations.month.packages')}</span>
                  </div>
                  <div>
                    <span className="font-mono font-bold text-lg text-content-secondary">{fmtNum(monatVersendet.waves)}</span>
                    <span className="text-xs text-content-secondary ml-1">{t('operations.month.waves')}</span>
                  </div>
                  <div>
                    <span className="font-mono font-bold text-lg text-content-secondary">{monatVersendet.werktage || monatVersendet.tage_mit_daten}</span>
                    <span className="text-xs text-content-secondary ml-1">{t('operations.month.working_days')}</span>
                  </div>
                </div>
                <div className="text-[10px] text-content-tertiary mt-1.5">
                  {t('operations.month.avg_per_workday', { n: fmtNum(monatVersendet.avg_pro_tag) })}
                </div>
              </>
            ) : (
              <div className="text-sm text-content-tertiary">{t('common.no_data')}</div>
            )}
          </div>
        </div>
      </div>

      {/* ── AUFTRÄGE VS. PAKETE ────────────────────────────────── */}
      {auftragePaketeTrend && auftragePaketeTrend.trend?.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-content-secondary" />
              <h3 className="text-sm font-bold text-content">{t('operations.auftragePakete')}</h3>
            </div>
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Package size={16} className="text-[#338edc]" />
                <div className="text-[10px] font-semibold uppercase tracking-wider text-content-secondary">
                  {t('operations.avgAuftraegeTag')}
                </div>
              </div>
              <div className="text-2xl font-bold text-content font-mono">
                {auftragePaketeTrend.avg_auftraege ?? '\u2013'}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Truck size={16} className="text-[#338edc]" />
                <div className="text-[10px] font-semibold uppercase tracking-wider text-content-secondary">
                  {t('operations.avgPaketeTag')}
                </div>
              </div>
              <div className="text-2xl font-bold text-content font-mono">
                {auftragePaketeTrend.avg_pakete ?? '\u2013'}
              </div>
            </div>
          </div>

          {/* Chart */}
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={auftragePaketeTrend.trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
                <XAxis
                  dataKey="tag"
                  tickFormatter={(v) => {
                    const d = new Date(v + 'T12:00:00');
                    return d.toLocaleDateString('de-AT', { day: '2-digit', month: 'short' });
                  }}
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={{ stroke: 'var(--border-primary)' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={{ stroke: 'var(--border-primary)' }}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 8, fontSize: 12 }}
                  formatter={(value, name) => [
                    fmtNum(value),
                    name === 'auftraege' ? t('operations.auftraege') : t('operations.pakete'),
                  ]}
                  labelFormatter={(l) => {
                    const d = new Date(l + 'T12:00:00');
                    return d.toLocaleDateString('de-AT', { day: '2-digit', month: 'long' });
                  }}
                />
                <Legend
                  formatter={(value) => value === 'auftraege' ? t('operations.auftraege') : t('operations.pakete')}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Line type="monotone" dataKey="auftraege" stroke="#338edc" strokeWidth={2} dot={false} name="auftraege" />
                <Line type="monotone" dataKey="pakete" stroke="#22D3EE" strokeWidth={2} dot={false} name="pakete" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── WEIGHT TREND CHART ──────────────────────────────── */}
      {weightTrend.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Weight className="w-4 h-4 text-content-secondary" />
              <div>
                <h3 className="text-sm font-bold text-content">{t('performance.paketgewicht')}</h3>
                <p className="text-[10px] text-content-secondary mt-0.5">{t('performance.basedOnDays', { days: 30 })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-1.5 rounded-full" style={{ backgroundColor: '#338edc' }} />
                <span className="text-content-tertiary">{t('performance.avgGewicht')} (kg)</span>
              </div>
            </div>
          </div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weightTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradWeight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#338edc" stopOpacity={0.1} />
                    <stop offset="100%" stopColor="#338edc" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                <XAxis dataKey="tag"
                  tickFormatter={val => { const d = new Date(val); return d.toLocaleDateString('de-AT', { day: '2-digit', month: 'short' }); }}
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false}
                  interval="preserveStartEnd" />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false}
                  tickFormatter={v => `${v} kg`} domain={[0, 'auto']} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    const fmtLabel = label ? new Date(label).toLocaleDateString('de-AT', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
                    return (
                      <div className="bg-surface-elevated border border-line-hover rounded-card px-3 py-2 shadow-xl text-xs">
                        <div className="font-medium text-content mb-1">{fmtLabel}</div>
                        <div className="text-content-secondary">{t('performance.avgGewicht')}: <span className="font-bold text-content">{d?.avg_gewicht?.toFixed(2)} kg</span></div>
                        <div className="text-content-tertiary">{fmtNum(d?.pakete)} {t('performance.packsCol') || 'Pakete'}</div>
                      </div>
                    );
                  }}
                />
                <Area type="monotone" dataKey="avg_gewicht" name={t('performance.avgGewicht')}
                  stroke="#338edc" strokeWidth={2} fill="url(#gradWeight)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── 2x2 CHART GRID ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

        {/* Aufträge pro Partner (Horizontal Stacked Bars, Top 15) */}
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <h3 className="text-sm font-bold text-content mb-1">{t('operations.chart.orders_per_partner')}</h3>
          <p className="text-[10px] text-content-secondary mb-4">{t('operations.chart.top15')}</p>
          <div style={{ height: 320 }}>
            {data?.pro_partner?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.pro_partner} layout="vertical"
                  margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                    axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={140}
                    tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                    axisLine={false} tickLine={false} />
                  <Tooltip content={<StackedTooltip fmtNum={fmtNum} t={t} />} />
                  <Bar dataKey="done" name={t('operations.chart.completed')} stackId="a" fill={CHART_COLORS.done} fillOpacity={0.8} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="todo" name={t('operations.chart.open')} stackId="a" fill={CHART_COLORS.todo} fillOpacity={0.6} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-content-tertiary text-sm">
                {loading ? t('common.loading') : t('common.no_data')}
              </div>
            )}
          </div>
        </div>

        {/* Tages-Verteilung (Ø Abgeschlossen pro Wochentag) */}
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <h3 className="text-sm font-bold text-content mb-1">{t('operations.chart.daily_distribution')}</h3>
          <p className="text-[10px] text-content-secondary mb-4">{t('operations.chart.avg_per_weekday')}</p>
          <div style={{ height: 320 }}>
            {hasData && data?.pro_wochentag?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.pro_wochentag} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
                  <XAxis dataKey="tag" tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                    axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                    axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<SimpleTooltip fmtNum={fmtNum} />} />
                  <Bar dataKey="durchschnitt" name={t('operations.chart.avg_completed')} fill="#06b6d4" fillOpacity={0.6} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-content-tertiary text-sm gap-2">
                {loading ? t('common.loading') : (
                  <>
                    <span>{t('operations.chart.data_collecting')}</span>
                    <span className="text-[10px]">{t('operations.chart.weekday_avg_note')}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Partner-Verteilung (Donut) */}
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <h3 className="text-sm font-bold text-content mb-1">{t('operations.chart.partner_distribution')}</h3>
          <p className="text-[10px] text-content-secondary mb-4">{t('operations.chart.packages_per_partner')}</p>
          <div style={{ height: 260 }} className="flex items-center">
            {data?.status_verteilung?.partner?.length > 0 ? (() => {
              const donutData = data.status_verteilung.partner.map((p, i) => ({
                name: p.name, value: p.value, color: PARTNER_COLORS[i % PARTNER_COLORS.length],
              }));
              const total = donutData.reduce((s, d) => s + d.value, 0);
              if (total === 0) return (
                <div className="flex items-center justify-center w-full text-content-tertiary text-sm">{t('common.no_data')}</div>
              );
              return (
                <div className="flex items-center gap-6 w-full">
                  <div className="flex-1" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                          paddingAngle={3} dataKey="value" strokeWidth={0}>
                          {donutData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.8} />)}
                        </Pie>
                        <Tooltip content={<SimpleTooltip fmtNum={fmtNum} />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3 min-w-[130px]">
                    {donutData.map(d => (
                      <div key={d.name} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: d.color }} />
                        <div>
                          <div className="text-xs text-content-secondary">{d.name}</div>
                          <div className="font-mono font-bold text-content text-sm">
                            {fmtNum(d.value)} <span className="text-content-tertiary text-[10px]">({Math.round(d.value / total * 100)}%)</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="border-t border-line-hover pt-2">
                      <div className="text-xs text-content-secondary">{t('operations.tooltip.total')}</div>
                      <div className="font-mono font-bold text-content text-sm">{fmtNum(total)}</div>
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div className="flex items-center justify-center w-full text-content-tertiary text-sm">
                {loading ? t('common.loading') : t('common.no_data')}
              </div>
            )}
          </div>
        </div>

        {/* Stunden-Verteilung (Heatmap) */}
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <h3 className="text-sm font-bold text-content mb-1">{t('operations.chart.hourly_distribution')}</h3>
          <p className="text-[10px] text-content-secondary mb-4">
            {isMultiDay
              ? t('operations.stundenAvgProTag', { tage: stundenAnzahlTage, days: stundenAnzahlTage })
              : t('operations.stundenEinzelTag')}
          </p>
          <div style={{ height: 260 }} className="flex flex-col justify-center">
            {data?.pro_stunde ? (() => {
              const hasHourData = data.pro_stunde.some(h => h.anzahl > 0);
              if (!hasHourData) return (
                <div className="flex flex-col items-center justify-center text-content-tertiary text-sm gap-2">
                  <span>{t('operations.chart.hourly_collecting')}</span>
                  <span className="text-[10px]">{t('operations.chart.hourly_note')}</span>
                </div>
              );
              // Only show working hours (6-22)
              const workHours = data.pro_stunde.filter(h => h.stunde >= 6 && h.stunde <= 21);
              return (
                <div>
                  {/* Hour grid */}
                  <div className="grid grid-cols-8 gap-1.5 mb-2">
                    {workHours.map(h => {
                      const displayVal = isMultiDay ? h.avg_pro_tag : h.anzahl;
                      const hasTrend = h.trend && h.trend.length >= 2;
                      return (
                        <div key={h.stunde} className="flex flex-col items-center gap-1">
                          <div className="w-full aspect-square rounded-lg flex flex-col items-center justify-center transition-colors relative overflow-hidden"
                            style={{ backgroundColor: heatColor(displayVal) }}
                            title={t('operations.chart.heatmap_title', { hour: String(h.stunde).padStart(2, '0'), count: fmtNum(isMultiDay ? h.avg_pro_tag : h.anzahl) })}>
                            <span className="font-mono font-bold text-white/90 relative z-10" style={{ fontSize: displayVal > 0 ? 11 : 10 }}>
                              {displayVal > 0 ? (isMultiDay ? `Ø ${fmtNum(displayVal)}` : fmtNum(displayVal)) : '\u00B7'}
                            </span>
                            {isMultiDay && hasTrend && displayVal > 0 && <HourSparkline data={h.trend} />}
                          </div>
                          <span className="text-[9px] text-content-tertiary font-mono">{String(h.stunde).padStart(2, '0')}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Legend */}
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <span className="text-[9px] text-content-tertiary">{t('operations.chart.few')}</span>
                    <div className="flex gap-0.5">
                      {[0.05, 0.15, 0.3, 0.5, 0.7, 0.9].map((v, i) => (
                        <div key={i} className="w-4 h-2.5 rounded-sm" style={{ backgroundColor: heatColor(v * maxHour) }} />
                      ))}
                    </div>
                    <span className="text-[9px] text-content-tertiary">{t('operations.chart.many')}</span>
                  </div>
                </div>
              );
            })() : (
              <div className="flex items-center justify-center text-content-tertiary text-sm">
                {loading ? t('common.loading') : t('common.no_data')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hour Sparkline (SVG) ────────────────────────────────────────

function HourSparkline({ data }) {
  if (!data || data.length < 2) return null;
  const w = 48, h = 14;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 2) - 1;
    return `${x},${y}`;
  }).join(' ');
  const trend = data[data.length - 1] - data[0];
  const color = trend > 5 ? '#10B981' : trend < -5 ? '#EF4444' : '#D1D5DB';
  return (
    <svg width={w} height={h} className="absolute bottom-1 left-1/2 -translate-x-1/2 opacity-70">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── KPI Card Component ──────────────────────────────────────────

function KpiCard({ label, value, sub, color, loading, progress, fmtNum }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-card relative overflow-hidden shadow-card">
      {/* Color accent */}
      <div className="absolute top-0 left-0 w-1 h-full rounded-l-card" style={{ backgroundColor: color }} />

      <div className="pl-3">
        <div className="text-[10px] text-content-secondary uppercase tracking-wider font-medium mb-1">{label}</div>
        <div className="font-mono font-bold text-2xl text-content leading-none">
          {loading && value == null ? (
            <div className="h-8 w-20 bg-surface-inset rounded animate-pulse" />
          ) : (
            fmtNum(value)
          )}
        </div>
        {sub && <div className="text-[10px] text-content-tertiary mt-1.5">{sub}</div>}

        {/* Progress bar */}
        {progress !== null && progress !== undefined && (
          <div className="mt-2 h-1.5 bg-surface-inset rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progress * 100, 100)}%`, backgroundColor: color }} />
          </div>
        )}
      </div>
    </div>
  );
}
